/* eslint-disable max-lines-per-function */
import React, { useEffect } from 'react'
import { authFetch, mountUrlRequest } from '~/services/fetch'
import {
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControlLabel,
  LinearProgress,
  Card,
  CardContent,
  CardActions,
  Typography,
  Divider,
  Switch,
  Box,
  Tooltip,
  CardMedia,
  CircularProgress,
  Skeleton,
  Fade,
  Stack,
} from '@mui/material'
import { useLang } from '~/hooks/useLang'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import ComputerIcon from '@mui/icons-material/Computer'
import Timeline from '@mui/lab/Timeline'
import TimelineItem from '@mui/lab/TimelineItem'
import TimelineSeparator from '@mui/lab/TimelineSeparator'
import TimelineContent from '@mui/lab/TimelineContent'
import TimelineOppositeContent, {
  timelineOppositeContentClasses,
} from '@mui/lab/TimelineOppositeContent'
import IUser from '~/features/User/interfaces/IUser'
import { formatDateHour } from '~/utils/format'
import { truncateText } from '~/utils/strings'
import DispatchTimelineStatus from '~/features/Dispatch/components/DispatchTimeline/components/DispatchTimelineStatus'
import CloseIcon from '@mui/icons-material/Close'
import SortIcon from '@mui/icons-material/Sort'
import { useHookstate } from '@hookstate/core'
import TimelineConnector from '@mui/lab/TimelineConnector'
import ForumIcon from '@mui/icons-material/Forum'
import dayjs from 'dayjs'
import { Download } from '@mui/icons-material'
import Zoom from 'react-medium-image-zoom'
import { downloadFile } from '~/utils/media'
import RefreshIcon from '@mui/icons-material/Refresh'

type DispatchTimelineItemsProps = {
  readonly id: number
  readonly is_edited: boolean
  readonly is_deleted: boolean
  readonly closing_event: boolean
  readonly is_from_device_removed: boolean
  readonly showEdits: boolean
  readonly showMore: boolean
  readonly isTruncate: boolean
  readonly date_updated_event: string
  readonly dispatch_origin: string | null
  readonly changed_by_user: {
    readonly name: string
    readonly representation_name: string
  }
  readonly previous_content:
  | readonly {
    readonly previous_comment: string
    readonly created_at: string
    readonly user: {
      readonly name: string
      readonly representation_name: string
    }
    readonly station: {
      readonly name: string
    }
  }[]
  | null
  readonly media_content: {
    readonly file_type: string
    readonly media_path: string
    readonly file_name: string
  }
  readonly event_name: string
  readonly event_at: number | string
  readonly date_event: string | Date
  readonly content: string
  readonly user: IUser
  readonly origin: {
    readonly name: string
  }
}

function TimelineLoadingSkeleton() {
  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      {[1, 2, 3, 4].map((item) => (
        <Fade key={item} in>
          <Box sx={{ mb: 4 }}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Skeleton variant="rectangular" width={60} height={20} />
              <Skeleton variant="circular" width={40} height={40} />
              <Box sx={{ flex: 1 }}>
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={120}
                  sx={{ borderRadius: 1 }}
                />
                <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                  <Skeleton variant="rectangular" width={80} height={16} />
                  <Skeleton variant="rectangular" width={100} height={16} />
                  <Skeleton variant="rectangular" width={60} height={16} />
                </Box>
              </Box>
            </Stack>
          </Box>
        </Fade>
      ))}
    </Box>
  )
}

// eslint-disable-next-line max-lines-per-function
export default function DispatchTimelineDialog({ open, chronology }) {
  const { translate } = useLang()
  const dispatchTimelineItems = useHookstate<
    readonly DispatchTimelineItemsProps[]
  >([])
  const loading = useHookstate(false)
  const filterComments = useHookstate(true)
  const filterEvents = useHookstate(true)
  const filterPreview = useHookstate(true)
  const sortChronology = useHookstate(false)
  const typeDocument = useHookstate<string | null>(null)

  // Função para buscar dados do backend
  const fetchTimelineData = () => {
    loading.set(true)
    authFetch({
      url: `/occurrence/${chronology}/timeline`,
      method: 'POST',
    }).then(
      ({ data }: { readonly data: readonly DispatchTimelineItemsProps[] }) => {
        dispatchTimelineItems.set(
          data.map((dataChronology) => ({
            ...dataChronology,
            showEdits: false,
            showMore: (Boolean(dataChronology.is_deleted) || Boolean(dataChronology.is_edited)),
            isTruncate: false,
          }))
        )
        loading.set(false)
      }
    )
  }
  useEffect(() => {
    fetchTimelineData()
  }, []) // Remove as dependências dos filtros

  const changeSortChronology = () => {
    sortChronology.set(!sortChronology.get())
  }

  function LoadingOverlay({ isLoading }: { readonly isLoading: boolean }) {
    if (!isLoading) return null

    return (
      <Fade in={isLoading}>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(2px)',
          }}
        >
          <CircularProgress size={60} thickness={4} />
          <Typography
            variant="body2"
            sx={{ mt: 2, color: 'text.secondary', fontWeight: 500 }}
          >
            {translate('Loading chronology...')}
          </Typography>
        </Box>
      </Fade>
    )
  }

  // Função para filtrar os itens no frontend
  const getFilteredItems = () => {
    return dispatchTimelineItems.value.filter((item) => {
      if (item.event_name === 'OCCURRENCE_COMMENT' && !filterComments.get()) {
        return false
      }
      if (item.event_name === 'PREVIEW_EVENT' && !filterPreview.get()) {
        return false
      }
      if (
        item.event_name !== 'OCCURRENCE_COMMENT' &&
        item.event_name !== 'PREVIEW_EVENT' &&
        !filterEvents.get()
      ) {
        return false
      }
      return true
    })
  }

  return (
    <Dialog
      open={open.get()}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      sx={{
        '& .MuiDialog-paper': {
          height: '90vh',
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle>
        <Typography
          variant="h5"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {translate('Chronology')}
          <IconButton
            title={translate('Close')}
            onClick={() => open.set(false)}
          >
            <CloseIcon />
          </IconButton>
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: '16px',
          }}
        >
          <Box
            sx={{
              width: '40%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <FormControlLabel
              sx={{ marginLeft: 0 }}
              control={
                <Switch
                  checked={filterComments.get()}
                  onChange={() => filterComments.set(!filterComments.value)}
                  disabled={loading.get()}
                />
              }
              label={translate('Comments')}
              labelPlacement="start"
            />

            <FormControlLabel
              sx={{ marginLeft: 0 }}
              control={
                <Switch
                  checked={filterEvents.get()}
                  onChange={() => filterEvents.set(!filterEvents.value)}
                  disabled={loading.get()}
                />
              }
              label={translate('Events')}
              labelPlacement="start"
            />
            <FormControlLabel
              sx={{ marginLeft: 0 }}
              control={
                <Switch
                  checked={filterPreview.get()}
                  onChange={() => filterPreview.set(!filterPreview.value)}
                  disabled={loading.get()}
                />
              }
              label={translate('Views')}
              labelPlacement="start"
            />
          </Box>
          <Box sx={{ paddingRight: '8px', display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={fetchTimelineData}
              startIcon={<RefreshIcon />}
              disabled={loading.get()}
            >
              {translate('Refresh')}
            </Button>
            <Button
              variant="contained"
              onClick={() => changeSortChronology()}
              endIcon={<SortIcon />}
              disabled={loading.get()}
            >
              {translate('Sort name', {
                code: sortChronology.get()
                  ? translate('Growing')
                  : translate('Decreasing'),
              })}
            </Button>
          </Box>
        </Box>

        {/* Barra de progresso linear no topo */}
        <Fade in={loading.get()}>
          <Box sx={{ width: '100%', mt: 1 }}>
            <LinearProgress
              color="primary"
              sx={{
                height: 3,
                borderRadius: 1,
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
              }}
            />
          </Box>
        </Fade>
      </DialogTitle>

      <DialogContent
        sx={{
          position: 'relative',
          minHeight: 400,
          height: 'calc(90vh - 200px)',
          overflow: 'auto'
        }}
      >
        {/* Overlay de loading */}
        <LoadingOverlay isLoading={loading.get()} />

        {/* Skeleton loading */}
        {loading.get() && <TimelineLoadingSkeleton />}

        {/* Mensagem quando nenhum filtro está ativo */}
        {!loading.get() &&
          !filterComments.get() &&
          !filterEvents.get() &&
          !filterPreview.get() && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '80%',
                mt: 4,
                px: 2,
                textAlign: 'center',
              }}
            >
              <Typography variant="body1" color="text.secondary">
                {translate('No filters active')}
              </Typography>
            </Box>
          )}

        {/* Conteúdo principal */}
        {!loading.get() &&
          (filterComments.get() || filterEvents.get() || filterPreview.get()) && (
            <Fade in={!loading.get()}>
              <Timeline
                sx={{
                  [`& .${timelineOppositeContentClasses.root}`]: {
                    flex: 0.2,
                  },
                }}
              >
                {getFilteredItems()
                  .map((item, index, array) => {
                    return (
                      <Fade key={index} in>
                        <TimelineItem>
                          {/* ...existing timeline item code... */}
                          <TimelineOppositeContent color="text.secondary">
                            <Typography variant={'body2'} sx={{ mt: 2 }}>
                              {formatDateHour(item.event_at).toFormatedDate}
                            </Typography>
                          </TimelineOppositeContent>
                          <TimelineSeparator sx={{ mt: 0.5 }}>
                            <DispatchTimelineStatus event={item.event_name} />
                            <TimelineConnector />
                          </TimelineSeparator>
                          <TimelineContent>
                            <Card
                              variant="elevation"
                              elevation={2}
                              sx={{
                                mb: 2,
                                width: 900,
                              }}
                            >
                              <CardContent>
                                <Typography
                                  variant={'body2'}
                                  style={{
                                    wordWrap: 'break-word',
                                    marginBottom: '0',
                                  }}
                                >
                                  {dispatchTimelineItems[
                                    index
                                  ].isTruncate.get() ? (
                                    item.content
                                  ) : !!item.is_deleted ? (
                                    `${translate('Comentário excluido')}`
                                  ) : !item.media_content ? (
                                    ` ${truncateText(item.content, 200)}`
                                  ) : item.media_content.file_type === 'jpeg' ||
                                    item.media_content.file_type === 'jpg' ||
                                    item.media_content.file_type === 'png' ? (
                                    <Zoom zoomMargin={50}>
                                      <CardMedia
                                        component="img"
                                        image={mountUrlRequest(
                                          `/occurrence-comment/get-media/${item.media_content.media_path}`
                                        )}
                                        alt={item.content}
                                        sx={{ width: '100px' }}
                                      />
                                    </Zoom>
                                  ) : (
                                    <Box
                                      display={'flex'}
                                      alignItems={'center'}
                                      justifyContent={'space-between'}
                                    >
                                      <Typography>
                                        {truncateText(
                                          `${item.media_content.file_name}`,
                                          30
                                        )}
                                      </Typography>
                                      <IconButton
                                        title={translate('Download file')}
                                        onClick={() =>
                                          downloadFile({
                                            mediaPath:
                                              item.media_content.media_path,
                                            fileType:
                                              item.media_content.file_type,
                                            typeDocument: typeDocument,
                                            isMount: false,
                                            fileName:
                                              item.media_content.file_name,
                                          })
                                        }
                                      >
                                        <Download />
                                      </IconButton>
                                    </Box>
                                  )}
                                  {item.content.length > 200 && (
                                    <Typography
                                      onClick={() => {
                                        dispatchTimelineItems[
                                          index
                                        ].isTruncate.set(
                                          !dispatchTimelineItems[
                                            index
                                          ].isTruncate.get()
                                        )
                                      }}
                                      sx={{ cursor: 'pointer' }}
                                      color="orange"
                                      variant="caption"
                                    >
                                      {' '}
                                      {!dispatchTimelineItems[
                                        index
                                      ].isTruncate.get()
                                        ? translate('more')
                                        : ''}
                                    </Typography>
                                  )}
                                </Typography>
                              </CardContent>
                              {/* ...rest of existing card content... */}
                              <Divider />
                              <CardActions sx={{ flexWrap: 'wrap' }}>
                                {item.user.name && (
                                  <>
                                    <Tooltip
                                      title={translate('User')}
                                      placement="bottom"
                                    >
                                      <Typography variant={'body2'}>
                                        <AccountCircleIcon />
                                      </Typography>
                                    </Tooltip>
                                    <Typography
                                      title={
                                        item.user.representation_name
                                          ? item.user.representation_name
                                          : ''
                                      }
                                      variant="body2"
                                    >
                                      {`${truncateText(
                                        item.user.representation_name
                                          ? item.user.representation_name
                                          : '',
                                        17,
                                        ''
                                      )}`}
                                    </Typography>
                                  </>
                                )}

                                {item.origin.name && (
                                  <>
                                    <Tooltip
                                      title={translate('Station')}
                                      placement="bottom"
                                    >
                                      <Typography variant={'body2'}>
                                        <ComputerIcon />
                                      </Typography>
                                    </Tooltip>

                                    <Typography variant={'body2'}>
                                      {item.origin.name}
                                    </Typography>
                                  </>
                                )}

                                {item.dispatch_origin && (
                                  <>
                                    <Tooltip
                                      title={translate('Dispatch origin')}
                                      placement="bottom"
                                    >
                                      <Typography sx={{ mt: 0.7 }}>
                                        <ForumIcon />
                                      </Typography>
                                    </Tooltip>
                                    <Typography variant={'body2'}>
                                      {item.dispatch_origin}
                                    </Typography>
                                  </>
                                )}

                                {!!item.is_edited &&
                                  !!item.is_deleted === false && (
                                    <Typography variant="body2" sx={{ flexBasis: '100%', mt: 0.5 }}>
                                      {`• ${translate('edited')} ${translate(
                                        'by'
                                      )} ${item.changed_by_user.representation_name
                                        } - ${dayjs(item.date_updated_event)
                                          .format('DD-MM-YYYY HH:mm')}`
                                      }
                                    </Typography>
                                  )}
                                {!!item.is_deleted && (
                                  <Typography variant="body2" sx={{ flexBasis: '100%', mt: 0.5 }}>
                                    {`• ${translate('deleted')} ${translate(
                                      'by'
                                    )} ${item.changed_by_user.representation_name
                                      } - ${dayjs(item.date_updated_event)
                                        .format('DD-MM-YYYY HH:mm')}`
                                    }
                                  </Typography>
                                )}
                                {Boolean(item.closing_event) && (
                                  <Typography variant="body2" sx={{ flexBasis: '100%', mt: 0.5 }}>
                                    {`• ${translate('closing comment by', { origin: item.origin.name })}`}
                                  </Typography>
                                )}
                                {Boolean(item.is_from_device_removed) && (
                                  <Typography variant="body2" sx={{ flexBasis: '100%', mt: 0.5 }}>
                                    {`• ${translate('release comment by', { origin: item.origin.name })}`}
                                  </Typography>
                                )}
                              </CardActions>
                            </Card>

                            {item.showMore ? (
                              <Divider textAlign="left">
                                <Typography
                                  variant="caption"
                                  sx={{ cursor: 'pointer', pt: 3 }}
                                  onClick={() => {
                                    dispatchTimelineItems.filter((comment) => {
                                      if (comment.id.get() === item.id) {
                                        comment.showEdits.set(!comment.showEdits.get())
                                      }
                                    })
                                  }}
                                >
                                  ━━
                                  {' '}
                                  {getFilteredItems()[index].showEdits
                                    ? translate('Hide historic')
                                    : translate('Show historic')}
                                </Typography>
                              </Divider>
                            ) : (
                              <Divider />
                            )}

                            {/* ...rest of existing content (showEdits sections)... */}
                            {item.showMore && item.showEdits && (
                              <Card
                                variant="elevation"
                                elevation={2}
                                sx={{ ml: 5, mb: 3, width: 860 }}
                              >
                                <CardContent>
                                  <Typography
                                    variant={'body2'}
                                    style={{
                                      wordWrap: 'break-word',
                                      marginBottom: '0',
                                    }}
                                  >
                                    {item.content}
                                  </Typography>
                                </CardContent>
                                <Divider />
                                <CardActions>
                                  {item.origin.name && (
                                    <>
                                      <Typography variant={'body2'}>
                                        <AccountCircleIcon />
                                      </Typography>
                                      <Typography
                                        title={
                                          item.user.representation_name
                                            ? item.user.representation_name
                                            : ''
                                        }
                                        variant="body2"
                                      >
                                        {`${truncateText(
                                          item.user.representation_name
                                            ? item.user.representation_name
                                            : '',
                                          17,
                                          ''
                                        )}`}
                                      </Typography>
                                    </>
                                  )}

                                  {item.origin.name && (
                                    <>
                                      <Typography variant={'body2'}>
                                        <ComputerIcon />
                                      </Typography>
                                      <Typography variant={'body2'}>
                                        {item.origin.name}
                                      </Typography>
                                    </>
                                  )}

                                  {item.dispatch_origin && (
                                    <>
                                      <Typography>
                                        <ForumIcon />
                                      </Typography>
                                      <Typography variant={'body2'}>
                                        {item.dispatch_origin}
                                      </Typography>
                                    </>
                                  )}
                                </CardActions>
                              </Card>
                            )}



                            {item.showMore && item.showEdits &&
                              item.previous_content &&
                              item.previous_content.map((content, i) => {
                                return (
                                  <Card
                                    key={i}
                                    variant="elevation"
                                    elevation={2}
                                    sx={{ ml: 5, mb: 3, width: 860 }}
                                  >
                                    <CardContent>
                                      <Typography
                                        variant={'body2'}
                                        style={{
                                          wordWrap: 'break-word',
                                          marginBottom: '0',
                                        }}
                                      >
                                        {content.previous_comment}
                                      </Typography>
                                    </CardContent>
                                    <Divider />
                                    <CardActions>
                                      {item.previous_content &&
                                        item.previous_content.length - 1 ===
                                        i && (
                                          <>
                                            <Typography variant={'body2'}>
                                              <AccountCircleIcon />
                                            </Typography>
                                            <Typography variant={'body2'}>
                                              {item.previous_content &&
                                                item.previous_content.length - 1 ===
                                                i
                                                ? item.user.representation_name
                                                : content.user
                                                  .representation_name}
                                            </Typography>
                                          </>
                                        )}

                                      {item.previous_content &&
                                        item.previous_content.length - 1 ===
                                        i && (
                                          <>
                                            <Typography variant={'body2'}>
                                              <ComputerIcon />
                                            </Typography>
                                            <Typography variant={'body2'}>
                                              {item.previous_content &&
                                                item.previous_content.length - 1 ===
                                                i
                                                ? item.origin.name
                                                : content.station.name}
                                            </Typography>
                                          </>
                                        )}

                                      {item.dispatch_origin && (
                                        <>
                                          <Typography>
                                            <ForumIcon />
                                          </Typography>
                                          <Typography variant={'body2'}>
                                            {item.dispatch_origin}
                                          </Typography>
                                        </>
                                      )}
                                    </CardActions>
                                  </Card>
                                )
                              })}


                          </TimelineContent>
                        </TimelineItem>
                      </Fade>
                    )
                  })
                  .sort((element1, element2) =>
                    element1 && element2 && element1 > element2
                      ? sortChronology.get() === false
                        ? -1
                        : 1
                      : sortChronology.get() === false
                        ? 1
                        : -1
                  )}
              </Timeline>
            </Fade>
          )}
      </DialogContent>
    </Dialog>
  )
}
